import { useEffect, useState, useMemo, useCallback } from 'react';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import api from '../api/client';
import MarkdownText from '../components/MarkdownText';
import StatementAttachmentLinks from '../components/StatementAttachmentLinks';
import { taskNavRef } from '../utils/taskNav';
import { useTabIdentity } from '../hooks/useTabIdentity';
import { badgeFromLabel } from '../utils/tabIdentity';

/**
 * Все задачи с одним тегом одним запросом (catalog_print=1), для сверки вёрстки и печати.
 * По умолчанию — карта ЕГЭ; ?ege_on_map=0 — весь банк (как в каталоге у учителя).
 */
export default function TaskCatalogPrintPage() {
  const { tagSlug } = useParams();
  const slug = (tagSlug || '').trim();
  const [searchParams, setSearchParams] = useSearchParams();

  const catalogSortField = searchParams.get('catalog_sort') === 'source' ? 'source' : 'site';
  const catalogSortOrder = searchParams.get('catalog_order') === 'desc' ? 'desc' : 'asc';
  const showFullBank = searchParams.get('ege_on_map') === '0';

  const [tasks, setTasks] = useState([]);
  const [tagLabel, setTagLabel] = useState(slug);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const setCatalogSort = useCallback(
    (field, order) => {
      const p = new URLSearchParams(searchParams);
      p.set('catalog_sort', field);
      p.set('catalog_order', order);
      setSearchParams(p, { replace: true });
    },
    [searchParams, setSearchParams]
  );

  const catalogBackHref = useMemo(() => {
    const p = new URLSearchParams();
    if (slug) p.append('tag', slug);
    if (catalogSortField === 'source') p.set('catalog_sort', 'source');
    if (catalogSortOrder === 'desc') p.set('catalog_order', 'desc');
    p.set('ege_on_map', showFullBank ? '0' : '1');
    const q = p.toString();
    return q ? `/catalog?${q}` : '/catalog';
  }, [slug, catalogSortField, catalogSortOrder, showFullBank]);

  useEffect(() => {
    if (!slug) {
      setError('Не указан тег.');
      setLoading(false);
      setTasks([]);
      return;
    }

    let cancelled = false;
    setLoading(true);
    setError('');

    const qs = new URLSearchParams();
    qs.append('tag', slug);
    qs.set('include_statement', '1');
    qs.set('catalog_print', '1');
    qs.set('catalog_sort', catalogSortField);
    qs.set('catalog_order', catalogSortOrder);
    if (!showFullBank) {
      qs.set('ege_on_map', '1');
    }

    api
      .get(`/tasks/?${qs.toString()}`)
      .then((res) => {
        const body = res.data;
        const list = Array.isArray(body) ? body : body?.results ?? [];
        if (!cancelled) {
          setTasks(list);
          const firstTag = list[0]?.tags?.find((t) => t.slug === slug);
          if (firstTag?.name) setTagLabel(firstTag.name);
        }
      })
      .catch((err) => {
        if (!cancelled) {
          if (err?.response?.status === 401 || err?.response?.status === 403) {
            setError('Войдите в аккаунт, чтобы открыть печать с ответами.');
          } else {
            setError('Не удалось загрузить задачи.');
          }
          setTasks([]);
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [slug, catalogSortField, catalogSortOrder, showFullBank]);

  useTabIdentity(
    tagLabel && slug
      ? {
          title: `Печать: ${tagLabel}`,
          badge: badgeFromLabel(tagLabel, 'П'),
          colorKey: 'print',
        }
      : null,
  );

  if (!slug) {
    return (
      <div className="catalog-print-page">
        <p className="text-secondary">Не указан тег в адресе страницы.</p>
        <Link to="/catalog" className="catalog-print-back no-print">
          ← В каталог
        </Link>
      </div>
    );
  }

  return (
    <div className="catalog-print-page">
      <header className="catalog-print-header no-print">
        <div>
          <h1 className="catalog-print-title">Для печати: {tagLabel}</h1>
          <p className="catalog-print-lead text-secondary">
            Условия с разметкой; после каждой задачи — строка с ответом (начинается с «# »). Печать браузера: Ctrl+P.
          </p>
        </div>
        <div className="catalog-print-header-actions no-print">
          <div className="catalog-sort-row catalog-print-sort-row">
            <div className="form-group catalog-sort-field">
              <label htmlFor="print-catalog-sort-field">Сортировка</label>
              <select
                id="print-catalog-sort-field"
                className="form-select"
                value={catalogSortField}
                onChange={(e) =>
                  setCatalogSort(e.target.value === 'source' ? 'source' : 'site', catalogSortOrder)
                }
              >
                <option value="site">Номер на сайте</option>
                <option value="source">Номер в источнике</option>
              </select>
            </div>
            <div className="form-group catalog-sort-field">
              <label htmlFor="print-catalog-sort-order">Направление</label>
              <select
                id="print-catalog-sort-order"
                className="form-select"
                value={catalogSortOrder}
                onChange={(e) =>
                  setCatalogSort(
                    catalogSortField,
                    e.target.value === 'desc' ? 'desc' : 'asc'
                  )
                }
              >
                <option value="asc">По возрастанию</option>
                <option value="desc">По убыванию</option>
              </select>
            </div>
          </div>
          <Link to={catalogBackHref} className="btn btn-outline btn-sm catalog-print-back">
            ← В каталог
          </Link>
        </div>
      </header>

      {error && <div className="alert alert-error no-print">{error}</div>}

      {loading ? (
        <p className="text-secondary no-print">Загрузка…</p>
      ) : tasks.length === 0 ? (
        <p className="text-secondary">Нет опубликованных задач с этим тегом.</p>
      ) : (
        <>
          <p className="catalog-print-count no-print text-secondary">
            Задач: {tasks.length}
            {tasks.length >= 500 ? ' (показано не более 500 — см. лимит на сервере).' : ''}
          </p>
          <ol className="catalog-print-task-list">
            {tasks.map((task) => (
              <li key={task.id} className="catalog-print-task">
                <div className="catalog-print-task-head">
                  <span className="catalog-print-task-id">
                    {task.source_number != null && task.source_number !== ''
                      ? String(task.source_number)
                      : taskNavRef(task)}
                  </span>
                  <span className="catalog-print-task-title">{task.title || taskNavRef(task)}</span>
                </div>
                <StatementAttachmentLinks attachments={task.statement_attachments} variant="catalog" />
                {task.statement ? (
                  <MarkdownText className="statement-text" text={task.statement} />
                ) : (
                  <p className="text-secondary">Нет текста условия.</p>
                )}
                {task.expected_answer != null && String(task.expected_answer).trim() !== '' ? (
                  <p className="catalog-print-expected-answer">
                    # {task.expected_answer}
                  </p>
                ) : null}
              </li>
            ))}
          </ol>
        </>
      )}
    </div>
  );
}
